import React, { useEffect, useState, useContext } from "react";
import { Card, CardBody, CardHeader } from "reactstrap";
import { useHistory, useParams } from "react-router-dom";
import { CircularProgress, MenuItem } from "@mui/material";

import { ReactComponent as BackIcon } from "assets/img/svg/BackButton.svg";
import { ReactComponent as Table } from "assets/img/svg/Table.svg";

import { PermissionContext } from "appContext";
import AButton from "views/pages/manage/common/form-fields-mui/AButton";
import IconButtonWrapper from "views/pages/manage/common/form-fields-mui/IconButtonWrapper";
import { capitalize } from "views/pages/manage/common/utils/methods/commonMethods/utilityMethod";

import {
  fetchPaymentOrderInfo,
  fetchApprovalJourney,
  fetchVendorApprovalConfigs,
  approvalLevelMap,
  actionableApproverLevelMap,
  APPROVAL_STATUS_ENUM,
  editTransactionStatus,
  rejectionLevelMap,
  getMaxApproverLevel,
  fetchApprovalLevelDetails,
} from "./utils";

import PaymentStatusStepper from "./PaymentStatusStepper";
import StudentsMappedTable from "./StudentsMappedTable";
import VendorAccountCard from "./VendorAccountCard";
import LogsPanel from "./LogsPanel";
import ApproveDialog from "./ApproveDialog";
import RejectDialog from "./RejectDialog";
import { getVendorDetails, retrySAPsync } from "../helper";
import ExamPayRateTable from "./ExamPayRateTable";

const PaymentOrderInfo = () => {
  const { id } = useParams();
  const history = useHistory();

  const userRole = useContext(PermissionContext);

  const [vendorDetails, setVendorDetails] = useState(null);
  const [approvalJourney, setApprovalJourney] = useState([]);
  const [logs, setLogs] = useState([]);
  const [approvalConfigs, setApprovalConfigs] = useState(null);
  const [loading, setLoading] = useState(true);
  // Dialog state
  const [approveSuccessDialog, setApproveSuccessDialog] = useState({
    isOpen: false,
    data: null,
  });
  const [rejectDialog, setRejectDialog] = useState({
    isOpen: false,
    data: null,
  });
  // Students table state
  const [students, setStudents] = useState([]);
  const [studentsPage, setStudentsPage] = useState(0);
  const [studentsPageSize, setStudentsPageSize] = useState(10);
  const [studentsRowCount, setStudentsRowCount] = useState(0);
  const [totalAmt, setTotalAmt] = useState(vendorDetails?.totalAmount);

  // Role-based action enable/disable
  const [canApprove, setCanApprove] = useState(false);
  const [canReject, setCanReject] = useState(false);

  // Fetch students paginated
  const updateStudents = (vendorData, page, pageSize) => {
    const all = vendorData?.vendorPsidMappingList || [];
    setStudents(all.slice(page * pageSize, (page + 1) * pageSize));
    setStudentsRowCount(all.length);
  };

  // Fetch all data and update paginated tables
  const fetchAPIs = async (
    stuPage = studentsPage,
    stuPageSize = studentsPageSize,
  ) => {
    setLoading(true);
    try {
      // Step 1: Fetch payment order info and logs
      const [vendorRes, logsRes] = await Promise.all([
        fetchPaymentOrderInfo(id),
        fetchApprovalJourney(id, 1000, 0), // fetch all logs, slice in UI
      ]);
      const reqObj = {
        businessAreaIds: vendorRes?.businessAreaId
          ? [vendorRes?.businessAreaId]
          : [],
        yearIds: vendorRes?.academicYearId ? [vendorRes?.academicYearId] : [],
        vendorTypes: vendorRes?.vendorType ? [vendorRes?.vendorType] : [],
      };
      const res = await fetchApprovalLevelDetails(reqObj);
      let level = 0;
      if (res?.code === 200) {
        level = getMaxApproverLevel(
          res?.data?.data?.[0],
          logsRes?.auditLogsList,
        );
      }

      const vendorData = vendorRes || null;
      setVendorDetails(vendorData);
      setTotalAmt(vendorData?.actualAmount);
      // Update students table
      updateStudents(vendorData, stuPage, stuPageSize);

      // Update logs table
      const logsList =
        logsRes && logsRes.auditLogsList ? logsRes.auditLogsList : [];
      setLogs(logsList);
      setApprovalJourney(logsList);
      // Step 2: Fetch vendorData
      if (vendorData && vendorData.vendorId) {
        const response = await getVendorDetails(vendorData.vendorId);
        setVendorDetails((prev) => ({
          ...prev,
          bankAccount: response?.bankAccount,
          ifsc: response?.ifsc,
          bankName: response?.bankName,
          accountHolderName: response?.accountHolderName,
          accountType: response?.accountType,
          bankBranch: response?.bankBranch,
          preferredPaymentMethod: response?.preferredPaymentMethod,
          maxApprovalLevel: level,
        }));
      }

      // Step 3: Fetch approval configs using transaction Data
      let configsRes = null;
      if (
        vendorData &&
        vendorData.businessAreaId &&
        vendorData.academicYearId &&
        vendorData.vendorType
      ) {
        const approvalConfigPayload = {
          businessAreaIds: [vendorData.businessAreaId],
          yearIds: [vendorData.academicYearId],
          vendorTypes: [vendorData.vendorType],
        };
        configsRes = await fetchVendorApprovalConfigs(approvalConfigPayload);
        setApprovalConfigs(configsRes?.data?.data || []);
      } else {
        setApprovalConfigs([]);
      }

      // Role-based logic for Approve/Reject/Edit
      let enableApprove = false,
        enableReject = false;

      // Only allow actions for actionable statuses
      const currentLevel = vendorData
        ? actionableApproverLevelMap[vendorData.approvalStatus]
        : null;
      if (currentLevel && vendorData && configsRes?.data?.data?.length) {
        const config = configsRes.data.data[0];
        const levelRoles = config.roleLevelApprovals?.[currentLevel] || [];
        if (levelRoles.some((r) => r.roleDispVal === userRole.roleType)) {
          enableApprove = true;
          enableReject = true;
        }
        // Only L4 can edit
        // if (
        //   currentLevel === "LEVEL_4" &&
        //   userRole.roleType === "VNDR_CORPORATE"
        // ) {
        //   enableEdit = true;
        // }
      }
      setCanApprove(enableApprove);
      setCanReject(enableReject);
    } catch (err) {
      setVendorDetails(null);
      setApprovalJourney([]);
      setLogs([]);
      setApprovalConfigs([]);
      setCanApprove(false);
      setCanReject(false);
      setStudents([]);
      setStudentsRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  // On mount and when pagination changes
  useEffect(() => {
    if (!id) return;
    fetchAPIs(studentsPage, studentsPageSize);
  }, [id, userRole, studentsPage, studentsPageSize]);

  if (loading)
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 300 }}
      >
        <CircularProgress />
      </div>
    );

  if (!vendorDetails || Object.keys(vendorDetails).length === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 300 }}
      >
        <div className="text-danger text-bold">
          No transaction data found for this vendor.
        </div>
      </div>
    );
  }

  // Helper: build vendorPsidMappingList for L4
  const buildVendorPsidMappingList = () => {
    // Mark deleted rows (those not present in students but present in initial vendorDetails)
    const initialList = vendorDetails?.vendorPsidMappingList || [];
    const currentIds = students.map((s) => s.vendorPsidMappingId);
    const deletedRows = initialList
      .filter((row) => !currentIds.includes(row.vendorPsidMappingId))
      .map((row) => ({ ...row, isDeleted: true }));
    // Add current rows (with isDeleted: false)
    const mappedRows = students.map((row) => ({ ...row, isDeleted: false }));
    return [...mappedRows, ...deletedRows];
  };

  // Approve handler
  const handleApprove = async () => {
    const approvalStatus = approvalLevelMap[vendorDetails.approvalStatus];
    const payload = {
      approvalStatus,
      approvalRemarks: "",
      totalAmount: Number(totalAmt),
    };
    if (approvalStatus === "L4_APPROVED") {
      payload.vendorPsidMappingList = buildVendorPsidMappingList();
    }
    try {
      const res = await editTransactionStatus(vendorDetails?.id, payload);
      if (res?.code === 200) {
        setApproveSuccessDialog({
          isOpen: true,
          data: { ...res, ...vendorDetails, approvalStatus },
        });

        fetchAPIs(0, 10);
      }
    } catch (err) {
      // handle error (show toast or dialog)
    }
  };

  // Reject handler
  const handleReject = async (remarks = "") => {
    const approvalStatus = rejectionLevelMap[vendorDetails.approvalStatus];
    const payload = {
      approvalStatus,
      approvalRemarks: remarks,
      totalAmount: Number(totalAmt),
    };
    if (approvalStatus === "L4_REJECTED") {
      payload.vendorPsidMappingList = buildVendorPsidMappingList();
    }
    try {
      const res = await editTransactionStatus(vendorDetails?.id, payload);
      if (res?.code === 200) {
        setApproveSuccessDialog({
          isOpen: true,
          data: { ...res, ...vendorDetails, remarks, approvalStatus },
          approvalStatus,
        });

        fetchAPIs(0, 10);
      }
    } catch (err) {
      // handle error (show toast or dialog)
    }
  };

  // Reject dialog handler
  const handleRejectDialog = (remarks) => {
    handleReject(remarks);
    setRejectDialog({ isOpen: false, data: null });
  };

  const retrySapSync = async () => {
    const res = await retrySAPsync(vendorDetails?.id, vendorDetails.vendorType);
    if (res?.code === 200) {
      window.location.reload();
    }
  };

  return (
    <div className="vendor-payment-details-page px-4 py-3">
      {/* Header Section styled like VendorInfo.jsx */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center">
          <IconButtonWrapper onClick={() => history.goBack()}>
            <BackIcon />
          </IconButtonWrapper>
          <span
            className="text-bold ml-2"
            style={{ fontSize: 22, color: "#222" }}
          >
            {vendorDetails?.vendorName} ({vendorDetails?.panNumber})
          </span>
        </div>
        <Card className="p-2 text-sm flex-row align-items-center mb-0">
          <div style={{ color: "#AFAFAF" }}> Vendor Type: &nbsp;</div>
          <span className="text-bold">
            {capitalize(vendorDetails?.vendorType)}
          </span>
        </Card>
      </div>

      {/* Main Content: Left (65%) and Right (35%) */}
      <div className="d-flex" style={{ gap: 32 }}>
        {/* Left Side (65%) */}
        <div style={{ width: "65%" }}>
          {/* Vendor Details Card (single, non-repetitive) */}
          <Card className="mb-3">
            <CardBody className="px-4 py-3">
              <div className="text-bold mb-3" style={{ fontSize: 18 }}>
                Vendor details
              </div>

              <div
                className="d-flex flex-wrap"
                style={{ rowGap: 8, fontSize: "14px" }}
              >
                <div
                  className="d-flex flex-column mr-5"
                  style={{ minWidth: 180 }}
                >
                  <span className="text-muted" style={{ fontWeight: 500 }}>
                    Vendor Type
                  </span>
                  <span className="text-bold">
                    {vendorDetails?.vendorType || "-"}
                  </span>
                </div>
                <div
                  className="d-flex flex-column mr-5"
                  style={{ minWidth: 180 }}
                >
                  <span className="text-muted" style={{ fontWeight: 500 }}>
                    Vendor Name
                  </span>
                  <span className="text-bold">
                    {vendorDetails?.vendorName || "-"}
                  </span>
                </div>
                <div
                  className="d-flex flex-column mr-5"
                  style={{ minWidth: 180 }}
                >
                  <span className="text-muted" style={{ fontWeight: 500 }}>
                    Vendor ID
                  </span>
                  <span className="text-bold">
                    {vendorDetails?.vendorSapCode || "-"}
                  </span>
                </div>
                <div
                  className="d-flex flex-column mr-5"
                  style={{ minWidth: 180 }}
                >
                  <span className="text-muted" style={{ fontWeight: 500 }}>
                    PAN Card Number
                  </span>
                  <span className="text-bold">
                    {vendorDetails?.panNumber || "-"}
                  </span>
                </div>

                <div
                  className="d-flex flex-column mr-5"
                  style={{ minWidth: 180 }}
                >
                  <span className="text-muted" style={{ fontWeight: 500 }}>
                    Transaction Number
                  </span>
                  <span className="text-bold">
                    {vendorDetails?.transactionId || "-"}
                  </span>
                </div>
                <div
                  className="d-flex flex-column mr-5"
                  style={{ minWidth: 180 }}
                >
                  <span className="text-muted" style={{ fontWeight: 500 }}>
                    Approval Status
                  </span>
                  <span className="text-bold">
                    {APPROVAL_STATUS_ENUM[vendorDetails?.approvalStatus] ||
                      vendorDetails?.approvalStatus ||
                      "-"}
                  </span>
                </div>
                <div
                  className="d-flex flex-column mr-5"
                  style={{ minWidth: 180 }}
                >
                  <span className="text-muted" style={{ fontWeight: 500 }}>
                    Business Unit
                  </span>
                  <span className="text-bold">
                    {vendorDetails?.businessAreaDispValue ||
                      vendorDetails?.buCode ||
                      "-"}
                  </span>
                </div>
                <div
                  className="d-flex flex-column mr-5"
                  style={{ minWidth: 180 }}
                >
                  <span className="text-muted" style={{ fontWeight: 500 }}>
                    Year
                  </span>
                  <span className="text-bold">
                    {vendorDetails?.academicYearDispValue || "-"}
                  </span>
                </div>
              </div>
              <div className="mt-3">
                <AButton
                  variant="link"
                  size="xs"
                  className="no-ml-btn"
                  onClick={() =>
                    history.push(
                      `/admin/vendorInfo/view/${vendorDetails?.vendorId}`,
                    )
                  }
                >
                  Go to vendor dashboard &rarr;
                </AButton>
              </div>
            </CardBody>
          </Card>

          {/* Students Mapped Table Card */}
          {vendorDetails?.vendorType === "ADMISSION_ADVISOR" && (
            <Card className="mb-3">
              <CardBody className="px-4 py-3">
                <StudentsMappedTable
                  stream={vendorDetails?.academicGroupDispValue}
                  currApprovalStatus={vendorDetails.approvalStatus}
                  students={students}
                  setRows={setStudents}
                  totalAmount={totalAmt}
                  setTotalAmt={setTotalAmt}
                  userRole={userRole.roleType}
                  approvalConfigs={approvalConfigs}
                  page={studentsPage}
                  pageSize={studentsPageSize}
                  rowCount={studentsRowCount}
                  onPageChange={setStudentsPage}
                  onPageSizeChange={setStudentsPageSize}
                  loading={loading}
                />
              </CardBody>
            </Card>
          )}

          {vendorDetails?.vendorType === "INVIGILATOR" && (
            <ExamPayRateTable
              vendorDetails={vendorDetails}
              totalAmount={totalAmt}
            />
          )}

          <VendorAccountCard vendorDetails={vendorDetails} />

          {/* Logs / Audit Trail Card */}
          <Card className="mb-3">
            <CardBody className="px-4 py-3">
              <div className="text-bold mb-3" style={{ fontSize: 18 }}>
                Logs
              </div>
              <LogsPanel logs={logs} />
            </CardBody>
          </Card>
        </div>
        {/* Right Side (35%) sticky */}
        <div style={{ width: "35%", minWidth: 320 }}>
          <div style={{ position: "sticky", top: 24, zIndex: 10 }}>
            <Card className="mb-3">
              <CardBody className="px-4 py-3">
                <div className="text-bold mb-2" style={{ fontSize: 16 }}>
                  Request actions
                </div>

                <div className="d-flex flex-column gap-2 w-100">
                  <AButton
                    variant="primary_filled"
                    block
                    disabled={!canApprove || loading}
                    onClick={handleApprove}
                  >
                    Approve
                  </AButton>
                  <AButton
                    variant="danger_filled"
                    block
                    disabled={!canReject || loading}
                    onClick={() =>
                      setRejectDialog({ isOpen: true, data: vendorDetails })
                    }
                  >
                    Reject
                  </AButton>

                  {vendorDetails.approvalStatus === "SAP_SYNC_FAILED" && (
                    <AButton
                      variant="primary_filled"
                      block
                      onClick={retrySapSync}
                    >
                      Retry SAP Sync
                    </AButton>
                  )}
                </div>
              </CardBody>
            </Card>
            {/* Payment Request Status Card */}

            <PaymentStatusStepper
              approvalJourney={approvalJourney}
              maxApprovalLevel={vendorDetails?.maxApprovalLevel}
            />

            {/* Quick Links Card */}
            <Card className="mb-3">
              <CardHeader>Actions</CardHeader>
              <CardBody className="p-2">
                <MenuItem
                  className="d-flex py-2"
                  style={{ gap: "16px", fontWeight: 700 }}
                  onClick={() => {
                    window.open(
                      `${
                        window.location.origin
                      }/#/admin/vendorPsidMapping/view/${vendorDetails?.vendorId}/true/${vendorDetails?.vendorType}`,
                      "_blank",
                    );
                  }}
                >
                  <Table />
                  View Payment History
                </MenuItem>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>

      {/* Approval Dialogs */}
      {approveSuccessDialog.isOpen && (
        <ApproveDialog
          confirmDialog={approveSuccessDialog}
          setConfirmDialog={setApproveSuccessDialog}
        />
      )}
      {rejectDialog.isOpen && (
        <RejectDialog
          isOpen={rejectDialog.isOpen}
          data={rejectDialog.data}
          setRejectionDialog={setRejectDialog}
          rejectionHandler={handleRejectDialog}
        />
      )}
    </div>
  );
};

export default PaymentOrderInfo;
